import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Theme = "default" | "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** What `data-theme` value the sidebar should carry. Always "dark" for default + dark; "light" only when the user picked full-light mode. */
  sidebarTheme: "dark" | "light";
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "adva-theme";
const VALID: readonly Theme[] = ["default", "light", "dark"] as const;

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "default";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.includes(stored as Theme)) {
      return stored as Theme;
    }
  } catch {
    // ignore — localStorage unavailable
  }
  return "default";
}

function applyThemeToDocument(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // "default" = hybrid (light content, dark sidebar via the sidebar's own attribute)
  // "light"   = light content (sidebar will also flip via the sidebarTheme value)
  // "dark"    = dark content (sidebar stays dark)
  if (theme === "dark") {
    root.setAttribute("data-theme", "dark");
  } else {
    root.setAttribute("data-theme", "light");
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore — localStorage unavailable
    }
  }, [theme]);

  const sidebarTheme: "dark" | "light" = theme === "light" ? "light" : "dark";

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme: setThemeState, sidebarTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

const FALLBACK: ThemeContextValue = {
  theme: "default",
  setTheme: () => {},
  sidebarTheme: "dark",
};

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? FALLBACK;
}
